import React, { useState, useMemo } from 'react';
import {
  HelpCircle,
  Percent,
  Mail,
  Globe,
  BookOpen,
  FileText,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
} from 'lucide-react';
import { Input, Button, TextArea } from '../../shared';
import { useAdminFAQ } from '../../../hooks/useAdminFAQ';

const adminHelpTabs = [
  { id: 'admin', label: 'Admin Settings', icon: HelpCircle },
  { id: 'client', label: 'Client Help', icon: BookOpen },
];

const adminCategories = [
  { id: 'commission', label: 'Commission', icon: Percent },
  { id: 'email', label: 'Email Settings', icon: Mail },
  { id: 'site-settings', label: 'Site Settings', icon: Globe },
];

const clientHelpTabs = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'bookings', label: 'Bookings', icon: FileText },
  { id: 'payments', label: 'Payments', icon: FileText },
  { id: 'account', label: 'Account', icon: HelpCircle },
  { id: 'safety', label: 'Safety', icon: HelpCircle },
];

const AdminHelpSettings = () => {
  const [activeTab, setActiveTab] = useState('admin');
  const [clientSubTab, setClientSubTab] = useState('getting-started');
  const [adminSubTab, setAdminSubTab] = useState('commission');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'getting-started',
    order: 0,
    is_active: true,
  });

  const {
    faqs: apiFaqs,
    isLoading,
    createFAQ,
    updateFAQ,
    deleteFAQ,
    isCreating,
    isUpdating,
  } = useAdminFAQ({ per_page: 100 });

  const faqsByCategory = useMemo(() => {
    const grouped = {};
    apiFaqs.forEach((faq) => {
      const cat = faq.category || 'general';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(faq);
    });
    Object.keys(grouped).forEach((cat) => {
      grouped[cat].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return grouped;
  }, [apiFaqs]);

  const filterFaqs = (faqs) => {
    if (!searchQuery.trim()) return faqs;
    const q = searchQuery.toLowerCase();
    return faqs.filter(
      (faq) =>
        (faq.question || '').toLowerCase().includes(q) ||
        (faq.answer || '').toLowerCase().includes(q)
    );
  };

  const openCreateModal = (category) => {
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: category || (activeTab === 'client' ? clientSubTab : adminSubTab),
      order: 0,
      is_active: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      category: faq.category || 'getting-started',
      order: faq.order ?? 0,
      is_active: faq.is_active ?? true,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: 'getting-started',
      order: 0,
      is_active: true,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      question: formData.question.trim(),
      answer: formData.answer.trim(),
      category: formData.category,
      order: parseInt(formData.order, 10) || 0,
      is_active: formData.is_active,
    };
    if (editingFaq) {
      updateFAQ({ id: editingFaq.id, data });
    } else {
      createFAQ(data);
    }
    closeModal();
  };

  const handleDelete = (faq) => {
    if (window.confirm('Are you sure you want to delete this FAQ?')) {
      deleteFAQ(faq.id);
    }
  };

  const categoryOptions = useMemo(() => {
    if (activeTab === 'client') {
      return clientHelpTabs.map((t) => ({ value: t.id, label: t.label }));
    }
    return adminCategories.map((c) => ({ value: c.id, label: c.label }));
  }, [activeTab]);

  const currentCategoryFaqs =
    activeTab === 'client'
      ? faqsByCategory[clientSubTab] || []
      : faqsByCategory[adminSubTab] || [];
  const filteredDisplay = filterFaqs(currentCategoryFaqs);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Help Center</h2>
          <p className="text-sm text-gray-600">
            Manage FAQs and preview help content for clients
          </p>
        </div>
        <Button
          onClick={() => openCreateModal()}
          className="flex items-center gap-2 shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add FAQ
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {adminHelpTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'admin' && (
        <div className="space-y-6">
          <div className="border-b border-gray-200">
            <nav className="flex gap-4 overflow-x-auto pb-2">
              {adminCategories.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setAdminSubTab(tab.id)}
                    className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap py-2 border-b-2 -mb-0.5 transition-colors ${
                      adminSubTab === tab.id
                        ? 'border-black text-black'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {adminCategories.find((t) => t.id === adminSubTab)?.label}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openCreateModal(adminSubTab)}
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add FAQ
              </Button>
            </div>
            {isLoading ? (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="space-y-4">
                {filteredDisplay.map((faq) => (
                  <div
                    key={faq.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 mb-2">{faq.question}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                      {faq.id && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => openEditModal(faq)}
                            className="p-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-md"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(faq)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filteredDisplay.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No FAQs in this category. Click &quot;Add FAQ&quot; to create one.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'client' && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Manage FAQs shown to clients on the public Help Center page.
          </p>
          <div className="border-b border-gray-200">
            <nav className="flex gap-4 overflow-x-auto pb-2">
              {clientHelpTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setClientSubTab(tab.id)}
                    className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap py-2 border-b-2 -mb-0.5 transition-colors ${
                      clientSubTab === tab.id
                        ? 'border-black text-black'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {clientHelpTabs.find((t) => t.id === clientSubTab)?.label}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openCreateModal(clientSubTab)}
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add FAQ
              </Button>
            </div>
            {isLoading ? (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="space-y-4">
                {filteredDisplay.map((faq) => (
                  <div
                    key={faq.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 mb-2">{faq.question}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                      {faq.id && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => openEditModal(faq)}
                            className="p-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-md"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(faq)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filteredDisplay.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No FAQs in this category. Click &quot;Add FAQ&quot; to create one.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingFaq ? 'Edit FAQ' : 'Create FAQ'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                >
                  {categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question *
                </label>
                <Input
                  required
                  value={formData.question}
                  onChange={(e) =>
                    setFormData({ ...formData, question: e.target.value })
                  }
                  placeholder="Enter the question"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Answer *
                </label>
                <TextArea
                  required
                  value={formData.answer}
                  onChange={(e) =>
                    setFormData({ ...formData, answer: e.target.value })
                  }
                  placeholder="Enter the answer"
                  rows={5}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>
                <Input
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: e.target.value })
                  }
                  placeholder="0"
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                />
                <span className="text-sm text-gray-700">Active (visible to users)</span>
              </label>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating || isUpdating}
                >
                  {editingFaq ? 'Save Changes' : 'Create FAQ'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHelpSettings;
